import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiPlusBold, PiSealCheckDuotone, PiTrashBold } from 'react-icons/pi';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';

type ApproverAction = { value: string; label: string };

type Approver = {
    id: number;
    user: { id: number; name: string; email: string | null };
    actions: string[];
    all_actions: boolean;
    active: boolean;
};

type ApproversResponse = { data: Approver[]; actions: ApproverAction[] };

type Candidate = { id: number; name: string; email: string | null };

const QUERY_KEY = ['commissioning', 'approvers'];

function apiMessage(err: unknown): string | null {
    return (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? null;
}

export default function CommissionApproversPage() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [search, setSearch] = useState('');
    const [removeTarget, setRemoveTarget] = useState<Approver | null>(null);

    const query = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => api.get('/commissioning/approvers').then((r) => r.data as ApproversResponse),
    });

    const candidates = useQuery({
        queryKey: [...QUERY_KEY, 'candidates', search],
        queryFn: () => api.get('/commissioning/approvers/candidates', { params: { q: search } })
            .then((r) => r.data.data as Candidate[]),
        enabled: search.trim().length >= 2,
    });

    const refresh = () => {
        void qc.invalidateQueries({ queryKey: QUERY_KEY });
        void qc.invalidateQueries({ queryKey: ['commissioning', 'director-approval-approvers'] });
    };

    const add = useMutation({
        mutationFn: (userId: number) => api.post('/commissioning/approvers', { user_id: userId }),
        onSuccess: () => {
            toast.success(t('v2.commissioning.approvers.saved'));
            setSearch('');
            refresh();
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.commissioning.director_approval.failed')),
    });

    const update = useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: { actions?: string[]; active?: boolean } }) =>
            api.patch(`/commissioning/approvers/${id}`, payload),
        onSuccess: () => refresh(),
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.commissioning.director_approval.failed')),
    });

    const remove = useMutation({
        mutationFn: (id: number) => api.delete(`/commissioning/approvers/${id}`),
        onSuccess: () => {
            toast.success(t('v2.commissioning.approvers.removed'));
            setRemoveTarget(null);
            refresh();
        },
    });

    const actions = query.data?.actions ?? [];
    const approvers = query.data?.data ?? [];

    const toggleAction = (approver: Approver, action: string) => {
        const next = approver.actions.includes(action)
            ? approver.actions.filter((a) => a !== action)
            : [...approver.actions, action];
        if (next.length === 0) return;
        update.mutate({ id: approver.id, payload: { actions: next } });
    };

    return (
        <div className="space-y-6">
            <PageHeader title={t('v2.commissioning.approvers.title')} subtitle={t('v2.commissioning.approvers.subtitle')} />

            <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-zinc-50">{t('v2.commissioning.approvers.add')}</p>
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('v2.commissioning.approvers.search')}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                />
                {search.trim().length >= 2 && (
                    <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-100 dark:divide-zinc-800 dark:border-zinc-800">
                        {(candidates.data ?? []).map((user) => (
                            <li key={user.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                <span className="min-w-0 truncate">
                                    <span className="font-semibold">{user.name}</span>
                                    <span className="ml-2 text-xs text-gray-500">{user.email}</span>
                                </span>
                                <Button type="button" size="sm" variant="outline" isLoading={add.isPending} onClick={() => add.mutate(user.id)}>
                                    <PiPlusBold className="mr-1 size-3.5" aria-hidden />
                                    {t('v2.commissioning.approvers.add')}
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {query.isLoading && <LoadingState variant="table" />}

            {query.isSuccess && approvers.length === 0 && (
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                    {t('v2.commissioning.approvers.empty')}
                </p>
            )}

            <div className="grid gap-4 md:grid-cols-2">
                {approvers.map((approver) => (
                    <article
                        key={approver.id}
                        className={cn(
                            'rounded-xl border bg-white p-4 shadow-sm dark:bg-zinc-900',
                            approver.active ? 'border-gray-200 dark:border-zinc-700' : 'border-amber-300 opacity-75',
                        )}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-3">
                                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                    <PiSealCheckDuotone className="size-5" aria-hidden />
                                </span>
                                <div className="min-w-0">
                                    <p className="truncate font-semibold text-gray-900 dark:text-zinc-50">{approver.user.name}</p>
                                    <p className="truncate text-xs text-gray-500">{approver.user.email}</p>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => update.mutate({ id: approver.id, payload: { active: !approver.active } })}
                                    className={cn(
                                        'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                                        approver.active
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
                                            : 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300',
                                    )}
                                >
                                    {approver.active ? t('v2.commissioning.approvers.active') : t('v2.commissioning.approvers.inactive')}
                                </button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    aria-label={t('v2.commissioning.approvers.remove')}
                                    onClick={() => setRemoveTarget(approver)}
                                >
                                    <PiTrashBold className="size-3.5" aria-hidden />
                                </Button>
                            </div>
                        </div>

                        <p className="mt-4 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                            {t('v2.commissioning.approvers.actions')}
                            {approver.all_actions && ` · ${t('v2.commissioning.approvers.all_actions')}`}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {actions.map((action) => {
                                const on = approver.actions.includes(action.value);

                                return (
                                    <button
                                        key={action.value}
                                        type="button"
                                        onClick={() => toggleAction(approver, action.value)}
                                        className={cn(
                                            'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                                            on
                                                ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                                                : 'border-gray-200 text-gray-500 hover:border-gray-300 dark:border-zinc-700 dark:text-zinc-400',
                                        )}
                                    >
                                        {action.label}
                                    </button>
                                );
                            })}
                        </div>
                    </article>
                ))}
            </div>

            <ConfirmModal
                open={removeTarget != null}
                onClose={() => setRemoveTarget(null)}
                onConfirm={() => removeTarget && remove.mutate(removeTarget.id)}
                title={t('v2.commissioning.approvers.remove')}
                description={t('v2.commissioning.approvers.remove_confirm', { name: removeTarget?.user.name ?? '' })}
                confirmLabel={t('v2.commissioning.approvers.remove')}
                loading={remove.isPending}
            />
        </div>
    );
}
